import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  notificationsApi,
  type Notification,
  type NotificationListResponse,
  type PreferenceCategory,
  type EventCatalogResponse,
} from '@/lib/notifications.api'

/**
 * Typed data-access layer for the `/notifications` endpoints.
 *
 * Centralises the query keys + cache invalidation that were previously spread
 * across `hooks/useNotifications.ts` / `hooks/useUnreadCount.ts`. The
 * `notificationsApi` transport wrapper is reused verbatim inside every
 * queryFn/mutationFn.
 *
 * The query keys are reproduced EXACTLY as before so existing wiring keeps
 * working untouched:
 *   - the unread-count badge polls `['notifications', 'unread-count']`
 *   - the SSE stream (`useNotificationStream`) invalidates the broad
 *     `['notifications']` prefix on every pushed event, which refreshes the
 *     list, recent dropdown and unread-count badge in real time.
 */

/** Fully-resolved list filters passed straight to the `/notifications` endpoint. */
export interface NotificationListParams {
  unread?: boolean
  category?: string
  limit?: number
  offset?: number
}

export const notificationKeys = {
  /** Broad prefix matching every notification query — used by the SSE stream
   * and mutations for invalidation. Matches list, recent and unread-count. */
  all: ['notifications'] as const,
  list: (params: NotificationListParams) => ['notifications', 'list', params] as const,
  recent: (limit: number) => ['notifications', 'recent', limit] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
  preferences: ['notification-preferences'] as const,
  eventCatalog: ['notification-event-catalog'] as const,
}

export function useNotificationsList(
  params: NotificationListParams = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<NotificationListResponse>({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationsApi.list(params),
    enabled: options.enabled,
  })
}

export function useRecentNotifications(limit = 10) {
  return useQuery<NotificationListResponse>({
    queryKey: notificationKeys.recent(limit),
    queryFn: () => notificationsApi.list({ limit }),
  })
}

/** Unread-count badge poller. 5-minute interval, runs in the background too. */
export function useUnreadCount() {
  return useQuery<number>({
    queryKey: notificationKeys.unreadCount,
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 300_000,
    refetchIntervalInBackground: true,
  })
}

export function useNotificationPreferences() {
  return useQuery<PreferenceCategory[]>({
    queryKey: notificationKeys.preferences,
    queryFn: notificationsApi.getPreferences,
  })
}

export function useNotificationEventCatalog() {
  return useQuery<EventCatalogResponse>({
    queryKey: notificationKeys.eventCatalog,
    queryFn: notificationsApi.getEventCatalog,
  })
}

/* ------------------------------------------------------------------ *
 * Mutations
 *
 * Each owns its cache invalidation. Read/all/delete/bulk all touch the
 * notification feed, so they invalidate the broad `['notifications']` prefix
 * (same as before) which refreshes list + recent + unread-count together.
 * ------------------------------------------------------------------ */

export function useNotificationMutations() {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: notificationKeys.all })

  const markAsRead = useMutation({
    mutationFn: notificationsApi.markAsRead,
    onSuccess: invalidate,
  })

  const markAllAsRead = useMutation({
    mutationFn: notificationsApi.markAllAsRead,
    onSuccess: invalidate,
  })

  const deleteOne = useMutation({
    mutationFn: notificationsApi.delete,
    onSuccess: invalidate,
  })

  const bulkAction = useMutation({
    mutationFn: ({ ids, action }: { ids: string[]; action: 'read' | 'delete' }) =>
      notificationsApi.bulkAction(ids, action),
    onSuccess: invalidate,
  })

  return { markAsRead, markAllAsRead, deleteOne, bulkAction }
}

/** Toggle a notification preference. `channel` is a category- or event-key
 * string (the preferences tree toggles both). Invalidates preferences. */
export function useUpdatePreference() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ channel, enabled }: { channel: string; enabled: boolean }) =>
      notificationsApi.updatePreference(channel, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.preferences }),
  })
}

export type { Notification }
